import React, { useState } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Users, Award, AlertTriangle, CheckCircle, BookOpen, Target, Activity, FileText, Calendar } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

// COMPREHENSIVE DEMO DATA
const DEMO_DATA = {
  schoolOverview: {
    totalStudents: 650,
    averageGrade: 83.5,
    attendanceRate: 94.2,
    graduationRate: 96.8,
    studentsAtRisk: 45,
    studentsExcelling: 215
  },

  departmentPerformance: [
    { department: 'English', avgGrade: 85.2, students: 650, atRisk: 8, excelling: 45, trend: '+2.5%' },
    { department: 'Mathematics', avgGrade: 82.1, students: 650, atRisk: 12, excelling: 38, trend: '+1.8%' },
    { department: 'Science', avgGrade: 84.8, students: 650, atRisk: 10, excelling: 42, trend: '+3.2%' },
    { department: 'History', avgGrade: 81.5, students: 650, atRisk: 9, excelling: 35, trend: '+1.2%' },
    { department: 'Languages', avgGrade: 83.9, students: 450, atRisk: 6, excelling: 40, trend: '+2.8%' }
  ],

  yearGroupPerformance: [
    { yearGroup: 'Year 7', students: 125, avgGrade: 84.2, attendance: 95.1, atRisk: 8, excelling: 42 },
    { yearGroup: 'Year 8', students: 130, avgGrade: 83.8, attendance: 94.8, atRisk: 9, excelling: 40 },
    { yearGroup: 'Year 9', students: 128, avgGrade: 82.9, attendance: 93.5, atRisk: 11, excelling: 38 },
    { yearGroup: 'Year 10', students: 135, avgGrade: 83.1, attendance: 94.0, atRisk: 10, excelling: 45 },
    { yearGroup: 'Year 11', students: 132, avgGrade: 84.5, attendance: 95.2, atRisk: 7, excelling: 50 }
  ],

  atRiskStudents: [
    { id: 'stud_020', name: 'Emma Wilson', yearGroup: 'Year 9', avgGrade: 62.5, attendance: 85.2, concerns: 'Falling behind in Math and Science', lastIntervention: '2024-12-15' },
    { id: 'stud_021', name: 'Noah Garcia', yearGroup: 'Year 8', avgGrade: 64.8, attendance: 88.5, concerns: 'Poor attendance affecting performance', lastIntervention: '2024-12-10' },
    { id: 'stud_022', name: 'Sophia Lee', yearGroup: 'Year 10', avgGrade: 63.2, attendance: 87.0, concerns: 'Struggling with written assignments', lastIntervention: '2024-12-20' },
    { id: 'stud_023', name: 'Liam Brown', yearGroup: 'Year 7', avgGrade: 61.5, attendance: 86.8, concerns: 'Needs additional support in core subjects', lastIntervention: '2024-12-18' }
  ],

  interventions: [
    { id: 'int_001', type: 'Tutoring', students: 25, subject: 'Mathematics', frequency: '2x weekly', status: 'active', effectiveness: '85%' },
    { id: 'int_002', type: 'Study Skills', students: 18, subject: 'All', frequency: '1x weekly', status: 'active', effectiveness: '78%' },
    { id: 'int_003', type: 'Mentoring', students: 12, subject: 'N/A', frequency: 'Weekly', status: 'active', effectiveness: '92%' },
    { id: 'int_004', type: 'After-School Support', students: 30, subject: 'Various', frequency: 'Daily', status: 'active', effectiveness: '81%' }
  ],

  curriculumProgress: [
    { subject: 'English', unitsPlanned: 8, unitsCompleted: 6, percentComplete: 75, onTrack: true },
    { subject: 'Mathematics', unitsPlanned: 10, unitsCompleted: 7, percentComplete: 70, onTrack: true },
    { subject: 'Science', unitsPlanned: 9, unitsCompleted: 7, percentComplete: 78, onTrack: true },
    { subject: 'History', unitsPlanned: 6, unitsCompleted: 4, percentComplete: 67, onTrack: false },
    { subject: 'Languages', unitsPlanned: 8, unitsCompleted: 6, percentComplete: 75, onTrack: true }
  ],

  assessmentData: {
    totalAssessments: 145,
    gradedAssessments: 128,
    pendingGrading: 17,
    averageScore: 83.5,
    completionRate: 94.2
  }
};

const BriSchoolAcademicBootstrap = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const getGradeColor = (grade) => {
    if (grade >= 85) return 'text-success';
    if (grade >= 75) return 'text-info';
    if (grade >= 65) return 'text-warning';
    return 'text-danger';
  };

  const getProgressColor = (percent) => {
    if (percent >= 75) return 'bg-success';
    if (percent >= 60) return 'bg-warning';
    return 'bg-danger';
  };

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
        <div className="container-fluid">
          <span className="navbar-brand mb-0 h1">
            <BarChart3 className="me-2" size={32} style={{color: '#6f42c1'}} />
            Academic Performance Dashboard
          </span>
          <div className="text-end">
            <small className="text-muted">BriSchool Analytics</small>
          </div>
        </div>
      </nav>

      {/* Navigation */}
      <div className="bg-white border-bottom">
        <div className="container-fluid">
          <ul className="nav nav-pills py-2">
            <li className="nav-item">
              <button onClick={() => setActiveTab('overview')} className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}>
                <BarChart3 size={16} className="me-2" />Overview
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('departments')} className={`nav-link ${activeTab === 'departments' ? 'active' : ''}`}>
                <BookOpen size={16} className="me-2" />Departments
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('yeargroups')} className={`nav-link ${activeTab === 'yeargroups' ? 'active' : ''}`}>
                <Users size={16} className="me-2" />Year Groups
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('interventions')} className={`nav-link ${activeTab === 'interventions' ? 'active' : ''}`}>
                <Target size={16} className="me-2" />Interventions
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('curriculum')} className={`nav-link ${activeTab === 'curriculum' ? 'active' : ''}`}>
                <FileText size={16} className="me-2" />Curriculum
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid py-4">
        {activeTab === 'overview' && (
          <div>
            <h2 className="mb-4">School Performance Overview</h2>

            <div className="row g-4 mb-4">
              <div className="col-md-3">
                <div className="card text-white bg-primary shadow">
                  <div className="card-body">
                    <Users size={32} className="mb-2" />
                    <h6 className="opacity-75">Total Students</h6>
                    <h2>{DEMO_DATA.schoolOverview.totalStudents}</h2>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="card text-white bg-success shadow">
                  <div className="card-body">
                    <TrendingUp size={32} className="mb-2" />
                    <h6 className="opacity-75">Average Grade</h6>
                    <h2>{DEMO_DATA.schoolOverview.averageGrade}%</h2>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="card text-white bg-info shadow">
                  <div className="card-body">
                    <CheckCircle size={32} className="mb-2" />
                    <h6 className="opacity-75">Attendance Rate</h6>
                    <h2>{DEMO_DATA.schoolOverview.attendanceRate}%</h2>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="card text-white bg-warning shadow">
                  <div className="card-body">
                    <AlertTriangle size={32} className="mb-2" />
                    <h6 className="opacity-75">Students At Risk</h6>
                    <h2>{DEMO_DATA.schoolOverview.studentsAtRisk}</h2>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-lg-8">
                <div className="card shadow mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">Department Performance Summary</h5>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Department</th>
                          <th className="text-center">Avg Grade</th>
                          <th className="text-center">At Risk</th>
                          <th className="text-center">Excelling</th>
                          <th className="text-center">Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {DEMO_DATA.departmentPerformance.map(dept => (
                          <tr key={dept.department}>
                            <td className="fw-bold">{dept.department}</td>
                            <td className="text-center">
                              <span className={`fw-bold ${getGradeColor(dept.avgGrade)}`}>
                                {dept.avgGrade}%
                              </span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-danger">{dept.atRisk}</span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-success">{dept.excelling}</span>
                            </td>
                            <td className="text-center text-success fw-bold">
                              <TrendingUp size={16} className="me-1" />
                              {dept.trend}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="card shadow">
                  <div className="card-header">
                    <h5 className="mb-0">Assessment Overview</h5>
                  </div>
                  <div className="card-body">
                    <div className="row text-center">
                      <div className="col-md-3 mb-3">
                        <h6 className="text-muted">Total Assessments</h6>
                        <h2 className="text-primary">{DEMO_DATA.assessmentData.totalAssessments}</h2>
                      </div>
                      <div className="col-md-3 mb-3">
                        <h6 className="text-muted">Graded</h6>
                        <h2 className="text-success">{DEMO_DATA.assessmentData.gradedAssessments}</h2>
                      </div>
                      <div className="col-md-3 mb-3">
                        <h6 className="text-muted">Pending</h6>
                        <h2 className="text-warning">{DEMO_DATA.assessmentData.pendingGrading}</h2>
                      </div>
                      <div className="col-md-3 mb-3">
                        <h6 className="text-muted">Average Score</h6>
                        <h2 className="text-info">{DEMO_DATA.assessmentData.averageScore}%</h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card shadow mb-4">
                  <div className="card-header bg-warning text-white">
                    <h5 className="mb-0">
                      <AlertTriangle size={20} className="me-2" />
                      Students Requiring Attention
                    </h5>
                  </div>
                  <div className="list-group list-group-flush">
                    {DEMO_DATA.atRiskStudents.slice(0, 4).map(student => (
                      <div key={student.id} className="list-group-item">
                        <h6 className="mb-1">{student.name}</h6>
                        <p className="text-muted mb-1 small">{student.yearGroup}</p>
                        <div className="d-flex justify-content-between mb-1">
                          <small>Grade:</small>
                          <strong className="text-danger">{student.avgGrade}%</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <small>Attendance:</small>
                          <strong className="text-warning">{student.attendance}%</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card shadow">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">
                      <Award size={20} className="me-2" />
                      Key Metrics
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Graduation Rate</span>
                        <strong>{DEMO_DATA.schoolOverview.graduationRate}%</strong>
                      </div>
                      <div className="progress" style={{height: '10px'}}>
                        <div className="progress-bar bg-success" style={{width: `${DEMO_DATA.schoolOverview.graduationRate}%`}}></div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Students Excelling</span>
                        <strong>{DEMO_DATA.schoolOverview.studentsExcelling}</strong>
                      </div>
                      <small className="text-muted">33% of student body</small>
                    </div>

                    <div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>Intervention Programs</span>
                        <strong>{DEMO_DATA.interventions.length}</strong>
                      </div>
                      <small className="text-muted">Active support programs</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'departments' && (
          <div>
            <h2 className="mb-4">Department Performance Analysis</h2>

            <div className="row g-4">
              {DEMO_DATA.departmentPerformance.map(dept => (
                <div key={dept.department} className="col-md-6 col-lg-4">
                  <div className="card shadow h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <h5 className="card-title mb-0">{dept.department}</h5>
                        <span className="badge bg-success">{dept.trend}</span>
                      </div>

                      <div className="row g-2 mb-3">
                        <div className="col-6">
                          <div className="card bg-light">
                            <div className="card-body p-2 text-center">
                              <small className="text-muted d-block">Avg Grade</small>
                              <h4 className={`mb-0 ${getGradeColor(dept.avgGrade)}`}>{dept.avgGrade}%</h4>
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="card bg-light">
                            <div className="card-body p-2 text-center">
                              <small className="text-muted d-block">Students</small>
                              <h4 className="text-primary mb-0">{dept.students}</h4>
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="card bg-danger bg-opacity-10">
                            <div className="card-body p-2 text-center">
                              <small className="text-muted d-block">At Risk</small>
                              <h4 className="text-danger mb-0">{dept.atRisk}</h4>
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="card bg-success bg-opacity-10">
                            <div className="card-body p-2 text-center">
                              <small className="text-muted d-block">Excelling</small>
                              <h4 className="text-success mb-0">{dept.excelling}</h4>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="d-grid">
                        <button className="btn btn-outline-primary btn-sm">View Details</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'yeargroups' && (
          <div>
            <h2 className="mb-4">Year Group Performance</h2>

            <div className="card shadow">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Year Group</th>
                      <th className="text-center">Students</th>
                      <th className="text-center">Avg Grade</th>
                      <th className="text-center">Attendance</th>
                      <th className="text-center">At Risk</th>
                      <th className="text-center">Excelling</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEMO_DATA.yearGroupPerformance.map(year => (
                      <tr key={year.yearGroup}>
                        <td className="fw-bold">{year.yearGroup}</td>
                        <td className="text-center">{year.students}</td>
                        <td className="text-center">
                          <span className={`fw-bold ${getGradeColor(year.avgGrade)}`}>
                            {year.avgGrade}%
                          </span>
                        </td>
                        <td className="text-center text-info fw-bold">{year.attendance}%</td>
                        <td className="text-center">
                          <span className="badge bg-danger">{year.atRisk}</span>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-success">{year.excelling}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'interventions' && (
          <div>
            <h2 className="mb-4">Intervention Programs</h2>

            <div className="row g-4 mb-4">
              {DEMO_DATA.interventions.map(intervention => (
                <div key={intervention.id} className="col-md-6">
                  <div className="card shadow h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="card-title mb-1">{intervention.type}</h5>
                          <p className="text-muted mb-0">{intervention.subject}</p>
                        </div>
                        <span className="badge bg-success">{intervention.status.toUpperCase()}</span>
                      </div>

                      <div className="row g-2 mb-3">
                        <div className="col-4">
                          <div className="text-center">
                            <small className="text-muted d-block">Students</small>
                            <h4 className="text-primary mb-0">{intervention.students}</h4>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="text-center">
                            <small className="text-muted d-block">Frequency</small>
                            <p className="mb-0 small">{intervention.frequency}</p>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="text-center">
                            <small className="text-muted d-block">Effectiveness</small>
                            <h4 className="text-success mb-0">{intervention.effectiveness}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card shadow">
              <div className="card-header">
                <h5 className="mb-0">
                  <AlertTriangle className="me-2" size={20} />
                  Students Requiring Intervention
                </h5>
              </div>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Student</th>
                      <th>Year Group</th>
                      <th className="text-center">Grade</th>
                      <th className="text-center">Attendance</th>
                      <th>Concerns</th>
                      <th>Last Intervention</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEMO_DATA.atRiskStudents.map(student => (
                      <tr key={student.id}>
                        <td className="fw-bold">{student.name}</td>
                        <td>{student.yearGroup}</td>
                        <td className="text-center">
                          <span className="text-danger fw-bold">{student.avgGrade}%</span>
                        </td>
                        <td className="text-center">
                          <span className="text-warning fw-bold">{student.attendance}%</span>
                        </td>
                        <td>
                          <small>{student.concerns}</small>
                        </td>
                        <td>{student.lastIntervention}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'curriculum' && (
          <div>
            <h2 className="mb-4">Curriculum Progress Tracking</h2>

            <div className="card shadow">
              <div className="card-header">
                <h5 className="mb-0">Subject Progress Overview</h5>
              </div>
              <div className="card-body">
                {DEMO_DATA.curriculumProgress.map(subject => (
                  <div key={subject.subject} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h6 className="mb-0">{subject.subject}</h6>
                        <small className="text-muted">
                          {subject.unitsCompleted} of {subject.unitsPlanned} units completed
                        </small>
                      </div>
                      <div className="text-end">
                        <span className={`badge ${subject.onTrack ? 'bg-success' : 'bg-warning'}`}>
                          {subject.onTrack ? 'On Track' : 'Behind'}
                        </span>
                        <div>
                          <strong className={subject.percentComplete >= 70 ? 'text-success' : 'text-warning'}>
                            {subject.percentComplete}%
                          </strong>
                        </div>
                      </div>
                    </div>
                    <div className="progress" style={{height: '12px'}}>
                      <div 
                        className={`progress-bar ${getProgressColor(subject.percentComplete)}`}
                        style={{width: `${subject.percentComplete}%`}}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BriSchoolAcademicBootstrap;